/**
 * How one cart line's price should read.
 *
 * The three states are distinct claims about money, and collapsing any two of them is
 * what made the cart unverifiable: a discounted line that keeps its original price
 * reads as a discount that never applied, and a $0 line with no story behind it reads
 * as a bug rather than a benefit of membership.
 */
export type LinePrice =
  | { kind: "included" }
  | { kind: "discounted"; originalLabel: string; discountedLabel: string }
  | { kind: "plain"; label: string };

export type LinePriceInput = {
  /** The line's price BEFORE any discount, in cents. */
  totalCents: number;
  /** Cents this line's share of the offer takes off; `0` when nothing applies. */
  discountCents: number;
  /** Formatted `totalCents` — the original price. */
  totalLabel: string;
  /** Formatted price after the discount. Read only when `discountCents > 0`. */
  discountedLabel: string;
  /**
   * Only a one-time line can be "Included". A presale membership is a $0/mo
   * RECURRING line, and labelling that Included would promise a membership that
   * never starts costing anything — it renders its price plus its own
   * "Billed monthly from <launch>" caption instead.
   */
  isOneTime: boolean;
};

/**
 * Picks the state, most specific first.
 *
 * A 100%-off line is `discounted`, never `included`: its price is $0 for a reason the
 * buyer can remove, so it has to show $250.00 → $0.00 rather than silently reading
 * like something membership always covered.
 */
export function resolveLinePrice({
  totalCents,
  discountCents,
  totalLabel,
  discountedLabel,
  isOneTime,
}: LinePriceInput): LinePrice {
  if (discountCents > 0) {
    return { kind: "discounted", originalLabel: totalLabel, discountedLabel };
  }
  if (totalCents === 0 && isOneTime) {
    return { kind: "included" };
  }
  return { kind: "plain", label: totalLabel };
}
